import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2, Building2, Mail, Lock, ShieldCheck, Sparkles, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { useQuery } from '@tanstack/react-query';
import { Combobox } from '@/components/ui/combobox';
import api from '@/lib/api';
import { useLogin, useLogin2fa } from '@/hooks/useAuth';
import { notify } from '@/lib/toast';
import { ThrottleNotice } from '@/components/auth/ThrottleNotice';
import { isLoginThrottled, loginThrottleKey, type LoginThrottle } from '@/lib/throttle-notice';

type LoginFormData = {
  tenantSlug?: string;
  email: string;
  password: string;
};

const DEMO_CREDENTIALS = {
  tenantSlug: 'demo-store',
  email: 'admin@demo.com',
  password: 'Admin123!',
} as const;

// POS-I379: the demo store's roles for the drop-down (credentials only while the server flag DEMO_LOGIN=true)
interface DemoRolesResponse {
  credentials: boolean;
  roles: Array<{ userId: string; label: string; description: string | null; email?: string; password?: string }>;
}

export function LoginPage() {
  const { t } = useTranslation('auth');
  const loginSchema = useMemo(
    () =>
      z.object({
        tenantSlug: z.string().optional(),
        email: z.string().min(1, t('emailRequired')).email(t('invalidEmail')),
        password: z.string().min(1, t('passwordRequired')),
      }),
    [t],
  );
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [demoRoleId, setDemoRoleId] = useState('');
  const { data: demoRoles } = useQuery<DemoRolesResponse>({
    queryKey: ['demo-roles', DEMO_CREDENTIALS.tenantSlug],
    queryFn: () => api.get('/auth/demo-roles', { params: { tenant: DEMO_CREDENTIALS.tenantSlug } }).then((r) => r.data),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const demoRoleList = demoRoles?.credentials ? demoRoles.roles.filter((r) => r.email && r.password) : [];
  const selectedDemoRole = demoRoleList.find((r) => r.userId === demoRoleId);
  // POS-I184: a throttled attempt shows one inline notice and disables Sign In until the wait is over
  const [throttle, setThrottle] = useState<LoginThrottle | null>(null);
  const loginMutation = useLogin(
    (token) => setTwoFactorToken(token),
    (seconds) =>
      setThrottle({
        seconds,
        until: Date.now() + seconds * 1000,
        key: loginThrottleKey(getValues('tenantSlug'), getValues('email')),
      }),
  );
  useEffect(() => {
    if (!throttle) return undefined;
    const timer = window.setTimeout(() => setThrottle(null), Math.max(0, throttle.until - Date.now()));
    return () => window.clearTimeout(timer);
  }, [throttle]);
  const login2faMutation = useLogin2fa(() => {
    setTwoFactorToken(null);
    setCode('');
    setUseRecoveryCode(false);
  });

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    getValues,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      tenantSlug: '',
      email: '',
      password: '',
    },
  });

  // POS-I236: the wait applies to the business + email that was throttled; changing either lifts it
  const currentKey = loginThrottleKey(watch('tenantSlug'), watch('email'));
  const throttled = isLoginThrottled(throttle, currentKey, Date.now());
  const onSubmit = (data: LoginFormData) => {
    if (throttled) return;
    loginMutation.mutate(data);
  };

  const fillDemoCredentials = () => {
    setValue('tenantSlug', DEMO_CREDENTIALS.tenantSlug, { shouldValidate: true });
    setValue('email', DEMO_CREDENTIALS.email, { shouldValidate: true });
    setValue('password', DEMO_CREDENTIALS.password, { shouldValidate: true });
    setFocus('password');
  };

  const fillDemoRoleCredentials = () => {
    if (!selectedDemoRole) return;
    setValue('tenantSlug', DEMO_CREDENTIALS.tenantSlug, { shouldValidate: true });
    setValue('email', selectedDemoRole.email ?? '', { shouldValidate: true });
    setValue('password', selectedDemoRole.password ?? '', { shouldValidate: true });
    setFocus('password');
  };

  const copyField = async (field: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField((c) => (c === field ? null : c)), 1500);
    } catch {
      notify.error(null, t('copyFailed'));
    }
  };

  const onVerify2fa = (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorToken) return;
    const cleaned = code.trim();
    const valid = useRecoveryCode ? cleaned.length >= 6 : cleaned.replace(/\s/g, '').length === 6;
    if (!valid) return;
    const submitted = useRecoveryCode ? cleaned.toUpperCase() : cleaned.replace(/\s/g, '');
    login2faMutation.mutate({ twoFactorToken, code: submitted });
  };

  const verify2faDisabled =
    login2faMutation.isPending ||
    (useRecoveryCode ? code.trim().length < 6 : code.length !== 6);

  if (twoFactorToken) {
    return (
      <Card className="border-0 shadow-xl glass-card animate-scale-in">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="flex items-center gap-2 text-2xl font-bold">
            <ShieldCheck className="h-6 w-6 text-primary-text" /> {t('twoFactorAuth')}
          </CardTitle>
          <CardDescription>
            {useRecoveryCode
              ? t('twoFactorRecoveryPrompt')
              : t('twoFactorCodePrompt')}
          </CardDescription>
        </CardHeader>
        <form onSubmit={onVerify2fa}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {useRecoveryCode ? t('recoveryCode') : t('authenticationCode')}
              </Label>
              {useRecoveryCode ? (
                <Input
                  id="code"
                  autoFocus
                  placeholder="XXXXX-XXXXX"
                  className="h-11 text-center text-lg tracking-[0.2em] uppercase"
                  value={code}
                  maxLength={14}
                  onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9-]/g, ''))}
                />
              ) : (
                <Input
                  id="code"
                  inputMode="numeric"
                  autoFocus
                  placeholder="123456"
                  className="h-11 text-center text-lg tracking-[0.5em]"
                  value={code}
                  maxLength={6}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                />
              )}
              <button
                type="button"
                className="text-xs text-primary-text hover:text-primary-text"
                onClick={() => { setUseRecoveryCode((v) => !v); setCode(''); }}
              >
                {useRecoveryCode ? t('useAuthenticatorInstead') : t('useRecoveryCode')}
              </button>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 pt-2">
            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-primary via-primary/90 to-primary/80 hover:from-primary/90 hover:via-primary/80 hover:to-primary/70 text-primary-foreground font-semibold shadow-glow transition-all"
              disabled={verify2faDisabled}
            >
              {login2faMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t('verify')}
            </Button>
            <button
              type="button"
              className="text-center text-sm text-muted-foreground hover:text-foreground"
              onClick={() => { setTwoFactorToken(null); setCode(''); setUseRecoveryCode(false); }}
            >
              {t('backToLogin')}
            </button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-xl glass-card animate-scale-in">
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-2xl font-bold animate-fade-in-up">{t('loginWelcome')}</CardTitle>
        <CardDescription>{t('loginDashboardSubtitle')}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {throttled && throttle && <ThrottleNotice seconds={throttle.seconds} />}
          <div className="space-y-2">
            <Label htmlFor="tenantSlug" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('businessId')} <span className="font-normal normal-case text-muted-foreground/70">{t('businessIdOptional')}</span>
            </Label>
            <div className="relative">
              <Building2 className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="tenantSlug"
                placeholder="demo-store"
                className="ps-9 h-11"
                {...register('tenantSlug')}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('businessIdBlankHint')}
            </p>
            {errors.tenantSlug && (
              <p className="text-xs text-destructive-text">{errors.tenantSlug.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('email')}
            </Label>
            <div className="relative">
              <Mail className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="admin@mystore.com"
                className="ps-9 h-11"
                {...register('email')}
              />
            </div>
            {errors.email && (
              <p className="text-xs text-destructive-text">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('password')}
            </Label>
            <div className="relative">
              <Lock className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className="ps-9 pe-10 h-11"
                {...register('password')}
              />
              <button
                type="button"
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-destructive-text">{errors.password.message}</p>
            )}
            {/* POS-I185 */}
            <p className="text-end">
              <Link to="/forgot-password" className="text-xs font-medium text-primary-text hover:underline">
                {t('forgotPassword')}
              </Link>
            </p>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-4 pt-2">
          <Button
            type="submit"
            className="w-full h-11 bg-gradient-to-r from-primary via-primary/90 to-primary/80 hover:from-primary/90 hover:via-primary/80 hover:to-primary/70 text-primary-foreground font-semibold shadow-glow transition-all"
            disabled={loginMutation.isPending || throttled}
          >
            {loginMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {t('loginButton')}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            {t('dontHaveAccount')}{' '}
            <Link to="/register" className="font-medium text-primary-text hover:text-primary-text">
              {t('registerBusinessLink')}
            </Link>
          </p>

          {/* ── Demo account ── */}
          <div className="w-full rounded-xl border border-primary/30 bg-primary/10 p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary-text">
                <Sparkles className="h-3.5 w-3.5" /> {t('tryDemoStore')}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 border-primary/30 text-primary-text hover:bg-primary/10"
                onClick={fillDemoCredentials}
              >
                {t('useDemoLogin')}
              </Button>
            </div>
            {demoRoleList.length > 0 && (
              <div className="mb-3 space-y-1.5" data-testid="demo-role-picker">
                <Label htmlFor="demo-role" className="text-xs text-muted-foreground">{t('demoRole')}</Label>
                <div className="flex items-center gap-2">
                  <div id="demo-role" className="min-w-0 flex-1">
                    <Combobox
                      options={demoRoleList.map((r) => ({ value: r.userId, label: r.label, sublabel: r.description ?? undefined }))}
                      value={demoRoleId}
                      onChange={setDemoRoleId}
                      placeholder={t('demoRolePlaceholder')}
                      searchPlaceholder={t('demoRoleSearch')}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0 border-primary/30 text-primary-text hover:bg-primary/10"
                    disabled={!selectedDemoRole}
                    onClick={fillDemoRoleCredentials}
                  >
                    {t('useRoleLogin')}
                  </Button>
                </div>
                {selectedDemoRole?.description && (
                  <p className="truncate text-xs text-muted-foreground" title={selectedDemoRole.description}>{selectedDemoRole.description}</p>
                )}
              </div>
            )}
            <dl className="space-y-1.5">
              {([
                { field: 'tenantSlug', label: t('businessId'), value: DEMO_CREDENTIALS.tenantSlug },
                { field: 'email', label: t('email'), value: DEMO_CREDENTIALS.email },
                { field: 'password', label: t('password'), value: DEMO_CREDENTIALS.password },
              ] as const).map(({ field, label, value }) => (
                <div key={field} className="flex items-center justify-between gap-2 text-xs">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="flex items-center gap-1.5">
                    <code className="rounded bg-card/70 px-1.5 py-0.5 font-mono text-[11px] text-foreground">{value}</code>
                    <button
                      type="button"
                      aria-label={t('copyField', { field: label })}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => copyField(field, value)}
                    >
                      {copiedField === field
                        ? <Check className="h-3.5 w-3.5 text-success-text" />
                        : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="flex justify-center pt-1">
            <LanguageSwitcher size="sm" />
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
